/**
 * Permissions Configuration
 * 
 * Centralized role and permission definitions for the STORYBIBLE platform.
 * This file defines what actions each role can perform within a project.
 */

/**
 * Available roles in the system
 * @type {readonly string[]}
 */
export const ROLES = Object.freeze(['developer', 'admin', 'guest', 'custom'])

/**
 * Human-readable labels for roles (for UI display)
 * @type {Record<string, string>}
 */
export const ROLE_LABELS = Object.freeze({
  developer: 'Developer',
  admin: 'Admin',
  guest: 'Guest',
  custom: 'Custom',
})

/**
 * Role descriptions for tooltips and help text
 * @type {Record<string, string>}
 */
export const ROLE_DESCRIPTIONS = Object.freeze({
  developer: 'Full admin access plus access to Testing and Design pages',
  admin: 'Full access to all project features including member management',
  guest: 'Read-only access to view project content',
  custom: 'Custom permissions (currently same as guest)',
})

/**
 * Permission definitions for each role
 * 
 * Permissions:
 * - canRead: View entities and project content
 * - canCreate: Create new entities (characters, locations, events, shots, images)
 * - canEdit: Modify existing entities
 * - canDelete: Remove entities
 * - canManageMembers: Add/remove project members and change roles
 * - canDeleteProject: Permanently delete the project
 * - canAccessDevPages: Access Testing and Design pages (developer only)
 * 
 * @type {Record<string, Record<string, boolean>>}
 */
export const PERMISSIONS = Object.freeze({
  developer: Object.freeze({
    canRead: true,
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canManageMembers: true,
    canDeleteProject: true,
    canAccessDevPages: true,
  }),
  admin: Object.freeze({
    canRead: true,
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canManageMembers: true,
    canDeleteProject: true,
    canAccessDevPages: false,
  }),
  guest: Object.freeze({
    canRead: true,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canManageMembers: false,
    canDeleteProject: false,
    canAccessDevPages: false,
  }),
  custom: Object.freeze({
    // For now, same as guest - extensible for future custom role configurations
    canRead: true,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canManageMembers: false,
    canDeleteProject: false,
    canAccessDevPages: false,
  }),
})

/**
 * List of all permission keys
 * @type {readonly string[]}
 */
export const PERMISSION_KEYS = Object.freeze([
  'canRead',
  'canCreate',
  'canEdit',
  'canDelete',
  'canManageMembers',
  'canDeleteProject',
  'canAccessDevPages',
])

/**
 * Short permission names mapping to full permission keys
 * Used for the can() helper function shorthand syntax
 * @type {Record<string, string>}
 */
export const PERMISSION_MAP = Object.freeze({
  read: 'canRead',
  create: 'canCreate',
  edit: 'canEdit',
  delete: 'canDelete',
  manageMembers: 'canManageMembers',
  deleteProject: 'canDeleteProject',
  accessDevPages: 'canAccessDevPages',
})

/**
 * Check if a role has a specific permission
 * 
 * @param {string} role - The role to check (admin, guest, custom)
 * @param {string} permission - The permission to check (e.g., 'canCreate' or 'create')
 * @returns {boolean} - Whether the role has the permission
 * 
 * @example
 * hasPermission('admin', 'canCreate') // true
 * hasPermission('guest', 'create')    // false (uses short name)
 * hasPermission('guest', 'canRead')   // true
 */
export function hasPermission(role, permission) {
  if (!role || !permission) {
    return false
  }

  // Support both short and full permission names
  const fullPermission = PERMISSION_MAP[permission] || permission

  return PERMISSIONS[role]?.[fullPermission] ?? false
}

/**
 * Get all permissions for a role
 * 
 * @param {string} role - The role to get permissions for
 * @returns {Record<string, boolean> | null} - The permissions object or null if role not found
 * 
 * @example
 * getPermissions('admin') // { canRead: true, canCreate: true, ... }
 * getPermissions('guest') // { canRead: true, canCreate: false, ... }
 */
export function getPermissions(role) {
  return PERMISSIONS[role] || null
}

/**
 * Check if a role is valid
 * 
 * @param {string} role - The role to validate
 * @returns {boolean} - Whether the role is valid
 */
export function isValidRole(role) {
  return ROLES.includes(role)
}

/**
 * Get the default role for new project members
 * @returns {string} - The default role
 */
export function getDefaultRole() {
  return 'guest'
}

/**
 * Get the role label for display
 * 
 * @param {string} role - The role
 * @returns {string} - Human-readable label
 */
export function getRoleLabel(role) {
  return ROLE_LABELS[role] || role
}

/**
 * Get the role description
 * 
 * @param {string} role - The role
 * @returns {string} - Role description
 */
export function getRoleDescription(role) {
  return ROLE_DESCRIPTIONS[role] || ''
}

export default PERMISSIONS
